import type { NextApiRequest, NextApiResponse } from 'next'
import {
  type CreateInvoiceOrderClientRequest,
  type CreateInvoiceOrderDTO,
  type CreateOrderItemDTO,
  type CreateOrderResponse,
  type DiscountDTO,
  type Language,
  type Option,
  OrderStatus,
  type ShipmentDTO,
  type ShippingProviderName
} from 'ecosystem'
import {
  computeOrderTotalsFromGross,
  parseStoreToBackendOrderLines,
  useLocaleCredentials as setLocaleCredentials
} from 'api'
import storefrontApi from 'api/storefront-api'
import { handlerWithLogging } from '../../../logging'
import { ingridShippingProvider } from '../createOrderHandler/ingridShippingProvider'
import { nshiftShippingProvider } from '../createOrderHandler/nshiftShippingProvider'
import { buildShipmentFromDefault } from '../shippingProviders'
import { type InvoiceOrderHandlerOpts } from './types'

interface BuildInvoiceDraftArgs {
  opts: InvoiceOrderHandlerOpts
  storeId: string
  items: CreateOrderItemDTO[]
  cartDiscountCode: Option<DiscountDTO>
  shipping?: {
    provider: ShippingProviderName
    providerSessionId?: string
  }
}

const buildAndCreateDraft = async ({
  opts,
  storeId,
  items,
  cartDiscountCode,
  shipping
}: BuildInvoiceDraftArgs) => {
  // No shipping args: nothing has been quoted yet at DRAFT time, so these are
  // the items-only totals. `submitInvoiceOrderHandler` recomputes them with the
  // resolved `shippingCost` before flipping to CREATED — that recompute is what
  // makes `orderAmount` the freight-inclusive total the BE publishes.
  const { orderAmount, orderTaxAmount, orderDiscount } = computeOrderTotalsFromGross(items)

  const shipments: ShipmentDTO[] | undefined = shipping
    ? [
        buildShipmentFromDefault({
          provider: shipping.provider,
          shipmentId: shipping.providerSessionId
        }).shipment
      ]
    : undefined

  const payload: CreateInvoiceOrderDTO = {
    storeId,
    customerId: opts.customerId,
    status: OrderStatus.DRAFT,
    currency: opts.currencyCode,
    items,
    orderAmount,
    orderTaxAmount,
    ...(orderDiscount > 0 ? { orderDiscount } : {}),
    ...(shipments ? { shipments } : {}),
    ...(cartDiscountCode?.code ? { discountCode: cartDiscountCode.code } : {})
  }

  return storefrontApi.invoiceOrders.createInvoiceOrder(payload)
}

const simpleShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: InvoiceOrderHandlerOpts
) => {
  const { storeId, items, cartDiscountCode }: CreateInvoiceOrderClientRequest = JSON.parse(req.body)
  const grossItems = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const order = await buildAndCreateDraft({
    opts,
    storeId,
    items: grossItems,
    cartDiscountCode
  })

  res.status(200).json({ storefrontOrderResponse: order })
}

const postnordShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: InvoiceOrderHandlerOpts
) => {
  const { storeId, items, cartDiscountCode }: CreateInvoiceOrderClientRequest = JSON.parse(req.body)
  const grossItems = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const order = await buildAndCreateDraft({
    opts,
    storeId,
    items: grossItems,
    cartDiscountCode,
    shipping: { provider: 'POSTNORD' }
  })

  res.status(200).json({ storefrontOrderResponse: order })
}

const ingridShipping = (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: InvoiceOrderHandlerOpts
) => {
  void ingridShippingProvider(req, res, opts, async (ingridResponse) => {
    const { storeId, items, cartDiscountCode }: CreateInvoiceOrderClientRequest = JSON.parse(
      req.body
    )
    const grossItems = parseStoreToBackendOrderLines(items, cartDiscountCode)
    const order = await buildAndCreateDraft({
      opts,
      storeId,
      items: grossItems,
      cartDiscountCode,
      shipping: {
        provider: 'INGRID',
        providerSessionId: ingridResponse.session.checkout_session_id
      }
    })

    res.status(200).json({ storefrontOrderResponse: order, ingridResponse })
  })
}

const nshiftShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: InvoiceOrderHandlerOpts
) => {
  const nshiftResponse = await nshiftShippingProvider(req, opts)

  const { storeId, items, cartDiscountCode }: CreateInvoiceOrderClientRequest = JSON.parse(req.body)
  const grossItems = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const order = await buildAndCreateDraft({
    opts,
    storeId,
    items: grossItems,
    cartDiscountCode,
    shipping: {
      provider: 'NSHIFT',
      providerSessionId: nshiftResponse.sessionId
    }
  })

  res.status(200).json({ storefrontOrderResponse: order, nshiftResponse })
}

/**
 * POST /api/orders/invoice — creates a DRAFT invoice order.
 *
 * Branches by `shippingProviderConfig.provider`:
 * - DEFAULT  → no carrier integration, just persist DRAFT with no shipments.
 * - POSTNORD → BE persists with shipments[0].shippingProvider=POSTNORD; the
 *              carrier widget runs FE-side and pushes booking details via the
 *              update handler.
 * - INGRID   → create Ingrid checkout session, persist DRAFT with
 *              shipments[0].shipmentId set to the session id.
 * - NSHIFT   → create Nshift session, persist DRAFT with shipments[0].shipmentId
 *              set to the session id.
 *
 * Pricing: items and totals are stored gross (VAT-inclusive). `orderTaxAmount`
 * is the included-VAT portion (single semantics across UPFRONT and INVOICE per
 * FEATURE_B2B_ORDER_PLACEMENT.md §3.5). B2B ex-VAT display is a render-time
 * transformation on the FE, not a storage difference.
 */
export const createInvoiceOrderHandler = handlerWithLogging(
  async (req: NextApiRequest, res: NextApiResponse, opts: InvoiceOrderHandlerOpts) => {
    if (req.method !== 'POST') {
      res.status(405).json({ error: 'Method not supported.' })
      return
    }
    if (!opts.customerId) {
      res.status(401).json({ error: 'Unauthorized — invoice flow requires a signed-in customer.' })
      return
    }

    const locale = (req.cookies.NEXT_LOCALE as Language) || 'sv'
    setLocaleCredentials(locale)

    try {
      switch (opts.shippingProviderConfig?.provider ?? 'DEFAULT') {
        case 'INGRID':
          return ingridShipping(req, res, opts)
        case 'POSTNORD':
          return postnordShipping(req, res, opts)
        case 'NSHIFT':
          return nshiftShipping(req, res, opts)
        case 'DEFAULT':
          return simpleShipping(req, res, opts)
      }
    } catch (e) {
      res.status(400).json({ error: e instanceof Error ? e.message : 'Unknown error' })
    }
  }
)
